import React from 'react';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const formatCurrency = (v) =>
    v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const AMOUNT_SOURCE = {
    AUDIT: 'Payment audit (what Square took)',
    CYCLE: 'Billing cycle (no audit row)',
};
const PRICE_SOURCE = {
    CYCLE: 'Priced on the billing cycle',
    SUBSCRIPTION: "Subscription's current price (the cycle has none)",
};
const FEE_SOURCE = {
    WEBHOOK: 'Square payment webhook',
    BACKFILL: 'Square Payments API (backfill)',
};

const Field = ({ label, value, mono, hint }) => (
    <Grid size={{ xs: 12, sm: 6 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', ...(mono ? { fontFamily: 'monospace', fontSize: 13 } : {}) }}>
            {value == null || value === '' ? '—' : value}
        </Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Grid>
);

const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>{title}</Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Grid container spacing={1.5}>{children}</Grid>
    </Box>
);

/** Every field the monthly report returns for one charge. */
const SubscriptionChargeDetailsDialog = ({ charge, onClose }) => {
    const c = charge;
    return (
        <Dialog open={Boolean(c)} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            {c && (
                <>
                    <DialogTitle sx={{ pr: 6 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                                {c.iboName || c.iboNumber}
                            </Typography>
                            <Chip size="small" variant="outlined" label={c.chargeKind}
                                color={c.chargeKind === 'NEW' ? 'success' : 'secondary'} />
                            <Chip size="small" variant="outlined" label={c.cycleStatus} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {c.planName || 'Unknown plan'} · {c.periodKey} · charged {formatDateTime(c.chargedAt)}
                        </Typography>
                        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Section title="Amounts">
                            <Field label="Charged (gross)" value={formatCurrency(c.chargedAmount)}
                                hint={AMOUNT_SOURCE[c.amountSource] || (c.chargedAmount == null ? 'No amount recorded' : null)} />
                            <Field label="Square fee" value={c.squareFee != null ? formatCurrency(c.squareFee) : 'Not recorded yet'}
                                hint={FEE_SOURCE[c.squareFeeSource]} />
                            <Field label="Net" value={formatCurrency(c.netAmount)}
                                hint={c.netAmount == null ? 'Needs both the charge and the fee' : null} />
                            <Field label="Subscription price now" value={formatCurrency(c.subPriceNow)}
                                hint={PRICE_SOURCE[c.priceSource]} />
                            <Field label="Content" value={formatCurrency(c.contentPrice)} />
                            <Field label="Books" value={formatCurrency(c.bookSubtotal)} />
                            <Field label="Shipping" value={formatCurrency(c.shippingCost)} />
                            <Field label="Tax" value={formatCurrency(c.taxAmount)} />
                        </Section>

                        <Section title="Member">
                            <Field label="IBO name" value={c.iboName} />
                            <Field label="IBO number" value={c.iboNumber} mono />
                            <Field label="Team" value={c.teamName} />
                            <Field label="Upline platinum"
                                value={c.uplinePlatinumIbo
                                    ? `${c.uplinePlatinumIboName || '—'} (${c.uplinePlatinumIbo})`
                                    : null} />
                        </Section>

                        <Section title="Subscription">
                            <Field label="Subscription ID" value={c.subscriptionId} mono />
                            <Field label="Status" value={c.subscriptionStatus} />
                            <Field label="Plan" value={c.planName} />
                            <Field label="Started" value={formatDate(c.subscriptionStartedAt)} />
                            <Field label="Square subscription ID" value={c.squareSubscriptionId} mono />
                        </Section>

                        <Section title="Billing cycle & payment">
                            <Field label="Cycle ID" value={c.cycleId} mono />
                            <Field label="Period" value={c.periodKey} />
                            <Field label="Cycle status" value={c.cycleStatus}
                                hint={c.cycleStatus === 'SHIPPED' ? 'Book order raised (or none was due), not parcel status' : null} />
                            <Field label="Charged at" value={formatDateTime(c.chargedAt)} />
                            <Field label="Square payment ID" value={c.paymentId} mono />
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    Receipt
                                </Typography>
                                {c.receiptUrl ? (
                                    <Link href={c.receiptUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                                        {c.receiptNumber || 'Open in Square'}
                                    </Link>
                                ) : (
                                    <Typography variant="body2">—</Typography>
                                )}
                            </Grid>
                        </Section>

                        <Section title="Books">
                            <Field label="Bundle" value={c.bookBundle} />
                            <Field label="Sets" value={c.bookSets} />
                            <Field label="Book order" value={c.bookOrderNumber || (c.bookNote ? 'None — books were due' : null)} mono={Boolean(c.bookOrderNumber)} />
                            <Field label="Order status" value={c.bookOrderStatus} />
                            <Field label="Shipped" value={c.bookShippedAt ? formatDate(c.bookShippedAt) : null} />
                            <Field label="Delivered" value={c.bookDeliveredAt ? formatDate(c.bookDeliveredAt) : null} />
                            <Field label="Tracking" value={c.trackingNumber} mono />
                        </Section>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default SubscriptionChargeDetailsDialog;
